import { useRef, useEffect, useState } from 'react';
import css from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';

export default function FixTab() {
    const { useLang, useRouter } = useGlobal();
    const { locale } = useRouter();
    const { t } = useLang('vanliferoadtrips');
    const [fixH, setFixH] = useState(0);

    const tabsRef = useRef<HTMLUListElement>(null);
    const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const ignoreScrollRef = useRef(false);

    const tabs: any = ['uk', 'it', 'es'].includes(locale)
        ? [
              { text: t('fix_tab1'), id: '#vanlife-roadtrips-B', key: 0 },
              { text: t('fix_tab2'), id: '#vanlife-roadtrips-C', key: 1 },
              { text: t('fix_tab4'), id: '#vanlife-roadtrips-P', key: 3 },
              { text: t('fix_tab5'), id: '#vanlife-roadtrips-Q', key: 4 },
              { text: t('fix_tab6'), id: '#vanlife-roadtrips-R', key: 5 },
          ]
        : ['kr', 'eu', 'nl'].includes(locale)
        ? [
              { text: t('fix_tab1'), id: '#vanlife-roadtrips-B', key: 0 },
              { text: t('fix_tab2'), id: '#vanlife-roadtrips-C', key: 1 },
              { text: t('fix_tab4'), id: '#vanlife-roadtrips-P', key: 3 },
              { text: t('fix_tab6'), id: '#vanlife-roadtrips-R', key: 5 },
          ]
        : [
              { text: t('fix_tab1'), id: '#vanlife-roadtrips-B', key: 0 },
              { text: t('fix_tab2'), id: '#vanlife-roadtrips-C', key: 1 },
              { text: t('fix_tab3'), id: '#vanlife-roadtrips-O', key: 2 },
              { text: t('fix_tab4'), id: '#vanlife-roadtrips-P', key: 3 },
              { text: t('fix_tab5'), id: '#vanlife-roadtrips-Q', key: 4 },
              { text: t('fix_tab6'), id: '#vanlife-roadtrips-R', key: 5 },
          ];

    const [activeTab, setActiveTab] = useState(tabs[0]?.id);

    // touch 拖动 tab
    let originX = 0,
        startX = 0;
    const onTouchStart = (e: any) => {
        originX = parseFloat(tabsRef.current?.style.left || '0');
        startX = e.targetTouches[0]?.clientX;
    };
    const onTouchMove = (e: any) => {
        const moveX = e.targetTouches[0]?.clientX;
        const endX = originX + moveX - startX;
        const tabBox = tabsRef.current;
        if (!tabBox) return;
        const maxLeft = 0;
        const minLeft = Math.min(window.innerWidth - tabBox.offsetWidth, 0);
        tabBox.style.left = `${Math.max(Math.min(endX, maxLeft), minLeft)}px`;
    };

    // 滚动监听，高亮 tab
    useEffect(() => {
        const handleScroll = () => {
            if (ignoreScrollRef.current) return;

            let currentId = tabs[0]?.id;
            for (let tab of tabs) {
                const el = document.querySelector(tab.id);
                if (el) {
                    const top = el.getBoundingClientRect().top;
                    if (top - fixH <= window.innerHeight / 2) {
                        currentId = tab.id;
                    }
                }
            }
            if (currentId !== activeTab) {
                setActiveTab(currentId);

                // 自动滚动 tab 到可视区域
                const tabBox = tabsRef.current;
                const activeEl = tabBox?.querySelector(`.tab[href="${currentId}"]`);
                if (activeEl && tabBox) {
                    const activeRect = (activeEl as HTMLElement).getBoundingClientRect();
                    const left = parseFloat(tabBox.style.left || '0');
                    const minLeft = Math.min(window.innerWidth - tabBox.offsetWidth, 0);
                    const maxLeft = 0;
                    let newLeft = left;
                    if (activeRect.left < 0) {
                        newLeft = Math.min(left - activeRect.left, maxLeft);
                    } else if (activeRect.right > window.innerWidth) {
                        newLeft = Math.max(left - (activeRect.right - window.innerWidth), minLeft);
                    }
                    tabBox.style.left = `${newLeft}px`;
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeTab, fixH]);

    // 计算顶部 sticky 高度
    useEffect(() => {
        setTimeout(() => {
            let h = 0;
            ['.announcement-bar', '.activity-bar'].forEach((cls) => {
                const elem = document.querySelector(cls);
                if (elem) {
                    const style = window.getComputedStyle(elem);
                    if (style.position === 'sticky') h += elem.offsetHeight;
                }
            });
            setFixH(h);
        }, 500);
    }, []);

    return (
        <div className={`vanlife-roadtrips-fix-tab ${css['vanlife-roadtrips-fix-tab']}`} style={{ top: `${fixH}px` }}>
            <div className="tabs-wrapper">
                <ul
                    ref={tabsRef}
                    className="tabs-box"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    style={{ left: 0, position: 'relative', transition: 'left 0.3s' }}
                >
                    {tabs.map((tab: any) => (
                        <a
                            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                            href={tab.id}
                            key={tab.id}
                            onClick={(e) => {
                                setActiveTab(tab.id);
                                // 点击后短时间忽略 scroll 自动切换
                                ignoreScrollRef.current = true;
                                if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
                                clickTimeoutRef.current = setTimeout(() => {
                                    ignoreScrollRef.current = false;
                                }, 1200);
                            }}
                        >
                            {tab.text}
                        </a>
                    ))}
                </ul>
            </div>
        </div>
    );
}
